/**
 * Smart title case conversion that respects proper nouns and common capitalization rules
 */

// Words that should remain lowercase (unless they're the first word)
const LOWERCASE_WORDS = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'from',
    'in',
    'into',
    'nor',
    'of',
    'on',
    'onto',
    'or',
    'so',
    'the',
    'to',
    'up',
    'with',
    'yet',
    // Spanish articles and prepositions
    'el',
    'la',
    'los',
    'las',
    'un',
    'una',
    'unos',
    'unas',
    'de',
    'del',
    'y',
    'e',
    'o',
    'u',
    'en',
    'con',
    'por',
    'para',
    'sin',
    'sobre',
])

// Common acronyms that should remain uppercase
const KNOWN_ACRONYMS = new Set([
    'AI', 'FBI', 'CIA', 'USA', 'UK', 'UN', 'EU', 'NATO', 'NASA', 'DNA', 'RNA',
    'CEO', 'CFO', 'CTO', 'VP', 'HR', 'IT', 'PR', 'TV', 'CD', 'DVD', 'GPS',
    'HTTP', 'HTTPS', 'HTML', 'CSS', 'JS', 'API', 'SQL', 'XML', 'JSON', 'PDF',
])


/**
 * Check if a word should remain uppercase
 * Only known acronyms or Roman numerals should stay uppercase
 */
function shouldRemainUppercase(word: string): boolean {
    // Check if it's a known acronym
    if (KNOWN_ACRONYMS.has(word)) {
        return true
    }

    // Check if it's a Roman numeral - simplified to reduce complexity
    const romanNumeralPattern = /^(M{0,3})(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i
    if (word && romanNumeralPattern.test(word)) {
        return true
    }

    return false
}

/**
 * Convert text to smart title case
 * @param text - The text to convert
 * @returns Text in title case
 */
export function toTitleCase(text: string | null | undefined): string | null | undefined {
    if (!text || typeof text !== 'string') return text

    // If text is not all uppercase, assume it's already properly cased
    if (text !== text.toUpperCase()) {
        return text
    }

    // Split by spaces and process each word
    const words = text.toLowerCase().split(/\s+/)

    return words
        .map((word, index) => {
            // Handle empty strings
            if (!word) return word

            // Check if it's an uppercase word (like acronym) in original text
            const originalWord = text.split(/\s+/)[index]
            if (originalWord && shouldRemainUppercase(originalWord)) {
                return originalWord
            }

            // Handle hyphenated words (e.g., \"Twenty-One\")
            if (word.includes('-')) {
                return word
                    .split('-')
                    .map((part, i) => {
                        if (i === 0 || part.length > 0) {
                            return part.charAt(0).toUpperCase() + part.slice(1)
                        }
                        return part
                    })
                    .join('-')
            }

            // First word is always capitalized
            if (index === 0) {
                return word.charAt(0).toUpperCase() + word.slice(1)
            }

            // Check if it's a lowercase word (article, preposition, etc.)
            if (LOWERCASE_WORDS.has(word)) {
                return word
            }

            // Default or proper noun: capitalize first letter
            return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')
}

/**
 * Normalize author name (usually \"Lastname, Firstname\" or \"Firstname Lastname\")
 * @param author - The author name to normalize
 * @returns Normalized author name
 */
export function normalizeAuthorName(author: string | null | undefined): string | null | undefined {
    if (!author || typeof author !== 'string') return author

    // If not all uppercase, return as-is
    if (author !== author.toUpperCase()) {
        return author
    }

    // Handle \"LASTNAME, FIRSTNAME\" format
    if (author.includes(',')) {
        const parts = author.split(',').map((part) => part.trim())
        return parts.map((part) => toTitleCase(part)).join(', ')
    }

    // Handle regular name format
    return toTitleCase(author)
}

/**
 * Book-like object with title and author fields
 */
interface BookLike {
    title?: string | null
    author?: string | null
    [key: string]: unknown
}

/**
 * Normalize a book object's title and author
 * @param book - Book object with title and author
 * @returns Book with normalized title and author
 */
export function normalizeBookText<T extends BookLike>(book: T | null | undefined): T | null | undefined {
    if (!book) return book

    const result = { ...book }

    if (book.title && typeof book.title === 'string') {
        const normalized = toTitleCase(book.title)
        if (normalized) {
            result.title = normalized as T['title']
        }
    }

    if (book.author && typeof book.author === 'string') {
        const normalized = normalizeAuthorName(book.author)
        if (normalized) {
            result.author = normalized as T['author']
        }
    }

    return result
}
